// src/services/payroll/lohnjournalImportService.ts

/**
 * Service für den Import von DATEV-Lohnjournalen zur Rechnungserstellung
 *
 * Kapselt die vollständige Verarbeitungskette einer Lohnjournal-PDF:
 *   1. PDF analysieren und identifizieren (Beraternummer, Mandantennummer, Monat, Jahr, AN-Anzahl)
 *   2. Kunde über Beraternummer + Mandantennummer matchen
 *   3. Abrechnungsdaten in customer_payroll_data speichern (Upsert)
 *   4. Rechnungsentwurf erstellen bzw. bestehenden Entwurf ergänzen
 *
 * Der Service liefert je Datei ein strukturiertes Ergebnis zurück, statt UI-State zu setzen.
 * Dadurch kann dieselbe Logik für eine einzelne Datei und für einen Stapel verwendet werden.
 *
 * Die aufgerufenen Services (payrollPdfAnalyzerService, payrollDataService, invoiceService)
 * bleiben unverändert und werden 1:1 wiederverwendet.
 */

import { supabase } from '../../lib/supabaseClient';
import { payrollPdfAnalyzerService } from './payrollPdfAnalyzerService';
import { payrollDataService } from './payrollDataService';
import { invoiceService } from '../invoice/invoiceService';

const GERMAN_MONTHS: Record<number, string> = {
  1: 'Januar', 2: 'Februar', 3: 'März', 4: 'April', 5: 'Mai', 6: 'Juni',
  7: 'Juli', 8: 'August', 9: 'September', 10: 'Oktober', 11: 'November', 12: 'Dezember'
};

/**
 * Klartext-Bezeichnungen der Rechnungsstatus für Hinweistexte.
 * Unbekannte Werte werden unverändert durchgereicht.
 */
const STATUS_LABELS: Record<string, string> = {
  entwurf: 'Entwurf',
  freigegeben: 'Freigegeben',
  versendet: 'Versendet',
  importiert: 'Importiert'
};

export type LohnjournalImportStatus = 'erfolg' | 'warnung' | 'fehler';

/**
 * Ergebnis der Verarbeitung genau einer Lohnjournal-Datei
 */
export interface LohnjournalImportErgebnis {
  dateiname: string;
  status: LohnjournalImportStatus;
  meldung: string;
  kundenname: string | null;
  customerId: string | null;
  monat: number | null;
  jahr: number | null;
  anzahlArbeitnehmer: number | null;
  rechnungId: string | null;
  /** Zusätzliche Auffälligkeiten, die den Vorgang nicht verhindert haben */
  hinweise: string[];
}

/**
 * Fortschrittsmeldung während der Stapelverarbeitung
 */
export interface LohnjournalStapelFortschritt {
  /** 1-basierter Index der gerade verarbeiteten Datei */
  aktuelleDatei: number;
  gesamtDateien: number;
  dateiname: string;
  /** Aktueller Verarbeitungsschritt im Klartext */
  phase: string;
}

export interface LohnjournalStapelCallbacks {
  /** Wird bei jedem Verarbeitungsschritt aufgerufen */
  onFortschritt?: (fortschritt: LohnjournalStapelFortschritt) => void;
  /** Wird aufgerufen, sobald eine Datei fertig verarbeitet ist */
  onErgebnis?: (ergebnis: LohnjournalImportErgebnis) => void;
}

/**
 * Zusammenfassung eines abgeschlossenen Stapels
 */
export interface LohnjournalStapelBilanz {
  gesamt: number;
  erfolg: number;
  warnung: number;
  fehler: number;
}

/**
 * Interner Kontext einer Stapelverarbeitung.
 * bereitsVerarbeitet: Schlüssel "customerId|jahr|monat" -> Dateiname der ersten Datei,
 * die diese Kombination im laufenden Stapel bereits belegt hat.
 */
interface VerarbeitungsKontext {
  bereitsVerarbeitet: Record<string, string>;
  meldePhase: (phase: string) => void;
}

const leeresErgebnis = (dateiname: string): LohnjournalImportErgebnis => ({
  dateiname,
  status: 'fehler',
  meldung: '',
  kundenname: null,
  customerId: null,
  monat: null,
  jahr: null,
  anzahlArbeitnehmer: null,
  rechnungId: null,
  hinweise: []
});

/**
 * Liest den Status der Zielrechnung und erzeugt einen Hinweis, wenn diese
 * nicht (mehr) im Status "entwurf" ist. Reine Anzeige, kein Eingriff in die
 * Rechnungslogik.
 */
const pruefeZielrechnungStatus = async (rechnungId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('status')
      .eq('id', rechnungId)
      .maybeSingle();

    if (error || !data) return null;
    if (data.status === 'entwurf') return null;

    const label = STATUS_LABELS[data.status] || data.status;
    return `Die Zielrechnung hat den Status "${label}" und ist kein Entwurf. Bitte prüfen.`;
  } catch (error) {
    console.error('[LohnjournalImport] Status der Zielrechnung konnte nicht geprüft werden:', error);
    return null;
  }
};

/**
 * Verarbeitet genau eine Lohnjournal-Datei.
 * Wirft keine Exception nach außen, jeder Fehlerfall wird als Ergebnis zurückgegeben.
 */
const verarbeiteDatei = async (
  file: File,
  kontext: VerarbeitungsKontext
): Promise<LohnjournalImportErgebnis> => {
  const ergebnis = leeresErgebnis(file.name);

  try {
    // ============================================
    // Schritt 1: PDF analysieren und identifizieren
    // ============================================
    kontext.meldePhase('PDF wird analysiert...');

    const analysisResult = await payrollPdfAnalyzerService.analyzeAndIdentify(file);

    if (!analysisResult.success || !analysisResult.data) {
      ergebnis.status = 'fehler';
      ergebnis.meldung = analysisResult.error || 'PDF-Analyse fehlgeschlagen';
      return ergebnis;
    }

    const {
      beraternummer,
      mandantennummer,
      month,
      year,
      anzahlArbeitnehmer,
      anzahlArbeitnehmerMitEntgelt,
      hasNextMonthBeitragsnachweis,
      nextMonth,
      nextMonthYear
    } = analysisResult.data;

    ergebnis.monat = month;
    ergebnis.jahr = year;
    ergebnis.anzahlArbeitnehmer = anzahlArbeitnehmer;

    // ============================================
    // Schritt 2: Kunde per Beraternummer + Mandantennummer matchen
    // ============================================
    kontext.meldePhase(`Suche Kunde mit Ber.${beraternummer} / Mand.${mandantennummer}...`);

    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .select('id, company')
      .eq('beraternummer', beraternummer)
      .eq('mandantennummer', mandantennummer)
      .eq('deleted', false)
      .maybeSingle();

    if (customerError || !customer) {
      ergebnis.status = 'fehler';
      ergebnis.meldung = `Kein Kunde mit Beraternummer ${beraternummer} und Mandantennummer ${mandantennummer} gefunden.`;
      return ergebnis;
    }

    ergebnis.customerId = customer.id;
    ergebnis.kundenname = customer.company;

    kontext.meldePhase(`Kunde erkannt: ${customer.company} – ${GERMAN_MONTHS[month]} ${year}`);

    // Dublettenprüfung innerhalb des laufenden Stapels: gleiche Kombination aus
    // Kunde, Jahr und Monat wurde bereits durch eine frühere Datei verarbeitet.
    const dublettenSchluessel = `${customer.id}|${year}|${month}`;
    const vorgaengerDatei = kontext.bereitsVerarbeitet[dublettenSchluessel];
    if (vorgaengerDatei) {
      ergebnis.hinweise.push(
        `Kunde und Abrechnungsmonat wurden in diesem Stapel bereits durch "${vorgaengerDatei}" verarbeitet. Die zuletzt verarbeitete Datei überschreibt die Abrechnungsdaten.`
      );
    } else {
      kontext.bereitsVerarbeitet[dublettenSchluessel] = file.name;
    }

    // ============================================
    // Schritt 3: Abrechnungsdaten speichern
    // ============================================
    kontext.meldePhase(`Speichere Abrechnungsdaten für ${customer.company}...`);

    const saveResult = await payrollDataService.savePayrollData({
      customerId: customer.id,
      year,
      month,
      anzahlArbeitnehmer,
      anzahlArbeitnehmerMitEntgelt,
      hasNextMonthBeitragsnachweis,
      nextMonth,
      nextMonthYear,
      sourceFilename: file.name
    });

    if (!saveResult.success) {
      ergebnis.status = 'fehler';
      ergebnis.meldung = `Fehler beim Speichern der Abrechnungsdaten: ${saveResult.error}`;
      return ergebnis;
    }

    // ============================================
    // Schritt 4: Rechnungsentwurf erstellen (nur wenn Arbeitnehmer gefunden)
    // ============================================
    if (anzahlArbeitnehmer && anzahlArbeitnehmer > 0) {
      kontext.meldePhase(`Erstelle Rechnungsentwurf für ${customer.company}...`);

      const draftResult = await invoiceService.createDraft({
        customerId: customer.id,
        year,
        month,
        anzahlArbeitnehmer
      });

      if (draftResult.success) {
        ergebnis.rechnungId = draftResult.invoiceId || null;
        ergebnis.status = 'erfolg';
        ergebnis.meldung = `Rechnungsentwurf für ${customer.company} (${GERMAN_MONTHS[month]} ${year}) wurde erfolgreich erstellt.`;
      } else if (draftResult.invoiceId) {
        // Entwurf existiert vielleicht schon
        ergebnis.rechnungId = draftResult.invoiceId;
        ergebnis.status = 'erfolg';
        ergebnis.meldung = `Rechnungsentwurf für ${customer.company} (${GERMAN_MONTHS[month]} ${year}) existiert bereits. Abrechnungsdaten wurden aktualisiert.`;
      } else {
        ergebnis.status = 'fehler';
        ergebnis.meldung = `Abrechnungsdaten gespeichert, aber Rechnungsentwurf konnte nicht erstellt werden: ${draftResult.error}`;
        return ergebnis;
      }

      // Statushinweis zur Zielrechnung (reine Anzeige)
      if (ergebnis.rechnungId) {
        const statusHinweis = await pruefeZielrechnungStatus(ergebnis.rechnungId);
        if (statusHinweis) {
          ergebnis.hinweise.push(statusHinweis);
        }
      }

      return ergebnis;
    }

    ergebnis.status = 'warnung';
    ergebnis.meldung = `Abrechnungsdaten für ${customer.company} (${GERMAN_MONTHS[month]} ${year}) gespeichert, aber Anzahl Arbeitnehmer konnte nicht aus dem PDF ermittelt werden. Es wurde kein Rechnungsentwurf erstellt – bitte manuell anlegen.`;
    return ergebnis;

  } catch (error) {
    console.error('[LohnjournalImport] Fehler bei der Verarbeitung von', file.name, error);
    ergebnis.status = 'fehler';
    ergebnis.meldung = error instanceof Error ? error.message : 'Unbekannter Fehler beim Upload';
    return ergebnis;
  }
};

export const lohnjournalImportService = {

  /**
   * Verarbeitet einen Stapel von Lohnjournal-Dateien nacheinander.
   *
   * Bewusst sequenziell: pdf.js lädt jede Datei vollständig in den Arbeitsspeicher
   * des Browsers, parallele Verarbeitung würde bei vielen oder großen Journalen
   * den Speicher belasten und die DB-Schreibvorgänge unnötig verschachteln.
   *
   * Eine fehlerhafte Datei stoppt den Stapel nicht, sie wird protokolliert und
   * die Verarbeitung läuft weiter.
   */
  verarbeiteStapel: async (
    files: File[],
    callbacks?: LohnjournalStapelCallbacks
  ): Promise<LohnjournalImportErgebnis[]> => {
    const ergebnisse: LohnjournalImportErgebnis[] = [];
    const bereitsVerarbeitet: Record<string, string> = {};

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const kontext: VerarbeitungsKontext = {
        bereitsVerarbeitet,
        meldePhase: (phase: string) => {
          if (callbacks && callbacks.onFortschritt) {
            callbacks.onFortschritt({
              aktuelleDatei: i + 1,
              gesamtDateien: files.length,
              dateiname: file.name,
              phase
            });
          }
        }
      };

      const ergebnis = await verarbeiteDatei(file, kontext);
      ergebnisse.push(ergebnis);

      if (callbacks && callbacks.onErgebnis) {
        callbacks.onErgebnis(ergebnis);
      }
    }

    return ergebnisse;
  },

  /**
   * Bilanz über eine Ergebnisliste (Anzahl je Status)
   */
  bilanz: (ergebnisse: LohnjournalImportErgebnis[]): LohnjournalStapelBilanz => {
    let erfolg = 0;
    let warnung = 0;
    let fehler = 0;

    for (let i = 0; i < ergebnisse.length; i++) {
      const status = ergebnisse[i].status;
      if (status === 'erfolg') {
        erfolg++;
      } else if (status === 'warnung') {
        warnung++;
      } else {
        fehler++;
      }
    }

    return { gesamt: ergebnisse.length, erfolg, warnung, fehler };
  }
};
